import React from 'react';
import cn from '@/utils/cn';
import {
  EMPTY_STATE_CLASS, ForensicsLink, LoadingSkeleton, SortableTh, TABLE_WRAPPER_CLASS, TH_CLASS,
  buildRowClass, formatElapsed,
} from './tableShared';
import type { HeartbeatStatus, LiveSessionRow, SortState } from './types';

interface SessionsTableProps {
  isLoading: boolean;
  onSelectSession?: (sessionId: string) => void;
  onSort?: (key: string) => void;
  selectedSessionId?: string | null;
  sessions: LiveSessionRow[];
  sort?: SortState;
}

const HEARTBEAT_LABEL: Record<HeartbeatStatus, string> = {
  abandoned: 'Abandoned',
  healthy: 'Healthy',
  missing: 'Missing',
  stale: 'Stale',
};

const HEARTBEAT_CLASS: Record<HeartbeatStatus, string> = {
  abandoned: 'tw-bg-red-100 tw-text-red-700 tw-border-red-200',
  healthy: 'tw-bg-green-100 tw-text-green-700 tw-border-green-200',
  missing: 'tw-bg-gray-100 tw-text-gray-600 tw-border-gray-200',
  stale: 'tw-bg-yellow-100 tw-text-yellow-800 tw-border-yellow-200',
};

const HeartbeatBadge = ({ status }: { status: HeartbeatStatus }) => (
  <span
    aria-label={`Teacher heartbeat: ${HEARTBEAT_LABEL[status]}`}
    className={cn(
      'tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium',
      'tw-border tw-border-solid',
      HEARTBEAT_CLASS[status],
    )}
  >
    {HEARTBEAT_LABEL[status]}
  </span>
);

const ConnectedCell = ({ session }: { session: LiveSessionRow }) => {
  const { connected_student_count: connected, total_student_count: total } = session;
  const ratio = total > 0 ? Math.round((connected / total) * 100) : 0;
  return (
    <div className="tw-flex tw-flex-col tw-gap-1">
      <div className="tw-text-sm tw-text-gray-900">
        {connected.toLocaleString()} / {total.toLocaleString()}
      </div>
      <div className="tw-h-1.5 tw-w-full tw-bg-gray-100 tw-rounded-full tw-overflow-hidden">
        <div
          aria-hidden
          className="tw-h-full tw-bg-blue-500"
          style={{ width: `${Math.min(100, ratio)}%` }}
        />
      </div>
    </div>
  );
};

const SessionsTable = ({
  isLoading,
  onSelectSession,
  onSort,
  selectedSessionId = null,
  sessions,
  sort,
}: SessionsTableProps) => {
  if (isLoading) return <LoadingSkeleton />;

  if (sessions.length === 0) {
    return <div className={EMPTY_STATE_CLASS}>No active sessions right now.</div>;
  }

  // AIDEV-NOTE: Students + Heartbeat are Redis-derived, so server-side sort isn't supported on
  // those columns. Render plain `th`s — keep them visually consistent but not interactive.
  const renderSortable = (key: string, label: string) => {
    if (!sort || !onSort) return <th className={TH_CLASS}>{label}</th>;
    return (
      <SortableTh
        active={sort.key === key}
        direction={sort.dir}
        label={label}
        sortKey={key}
        onSort={onSort}
      />
    );
  };

  return (
    <div className={TABLE_WRAPPER_CLASS}>
      <table className="tw-w-full tw-border-collapse">
        <thead className="tw-bg-gray-50">
          <tr>
            {renderSortable('teacher', 'Teacher')}
            {renderSortable('program', 'Program')}
            {renderSortable('slide_show', 'Slideshow')}
            {renderSortable('started_at', 'Started')}
            <th className={TH_CLASS}>Students</th>
            <th className={TH_CLASS}>Heartbeat</th>
            <th className={TH_CLASS}><span className="tw-sr-only">Forensics</span></th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((session) => {
            const onClick = onSelectSession ? () => onSelectSession(session.session_id) : undefined;
            const isSelected = selectedSessionId === session.session_id;
            return (
              <tr
                key={session.session_id}
                aria-selected={isSelected || undefined}
                className={buildRowClass({ isSelected, onClick })}
                onClick={onClick}
              >
                <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-900">
                  {session.teacher.name ?? '—'}
                </td>
                <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-700">
                  {session.program.name ?? '—'}
                </td>
                <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-700">
                  {session.slide_show.name ?? '—'}
                </td>
                <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-700 tw-whitespace-nowrap">
                  {formatElapsed(session.started_at)} ago
                </td>
                <td className="tw-px-4 tw-py-3 tw-w-48">
                  <ConnectedCell session={session} />
                </td>
                <td className="tw-px-4 tw-py-3">
                  <HeartbeatBadge status={session.heartbeat_status} />
                </td>
                <td className="tw-px-4 tw-py-3">
                  <ForensicsLink eventsCount={session.events_count} sessionId={session.session_id} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default SessionsTable;
